<script setup lang="ts">

import { onMounted, computed } from "vue";
import { useOfferService } from "../Services/OfferService";
import { useBackButton } from "@app/Components/Utilities";
import { comma, formatDate, useParams } from "@library/Utilities";
import { OfferHeaderStyle } from "../Types/OfferHeaderStyle";
import type { IOfferTile } from "@domain/Marketplace/IOfferTile";

import OfferHeader from "../Components/Offer/OfferHeader.vue";
import OfferLocation from "../Components/Offer/OfferLocation.vue";
import OfferExpandableSection from "../Components/Offer/OfferExpandableSection.vue";
import { useViewModelInstance } from "../Types/MarketplaceViewModelStore";

const { offerId } = useParams();
const viewModel = await useViewModelInstance();
const service = useOfferService();
const marketplaceViewModel = await useViewModelInstance();
const model = await service.getOffer( offerId, marketplaceViewModel.getSearchBoundary() );
const offerTile = model as unknown as IOfferTile;

if( model?.locations != null )
{
    model.locations
        .sort( (a,b) => ( a.getDistance( viewModel.gpsLocation ) ?? 0 ) > ( b.getDistance( viewModel.gpsLocation ) ?? 0 ) ? 1 : -1 );
}

const nearestLocation = computed( () => 
{
    if( ( model?.locations ?? [] ).length == 0 ) return undefined;
    return model?.locations[ 0 ].getDistance( viewModel.gpsLocation );
}); 

onMounted( () => 
{
    useBackButton( true, true );
});

</script>

<template>

    <div class="h-full overflow-y-auto text-base tracking-wider">

        <div v-if="model == null">
            Unable to load offer
        </div>

        <div v-else>
            
            <OfferHeader :offer="offerTile" 
                         :header-style="OfferHeaderStyle.Full">
            </OfferHeader>

            <!-- NEAREST LOCATION -->
            <div v-if="nearestLocation"
                class="text-base 3xl:text-xl tracking-wider px-4 3xl:px-6 pb-6 flex items-end gap-2 3xl:gap-3">
                <Icon name="map-pin" class="text-3xl 3xl:text-4xl" />
                Nearest to you is {{ comma( nearestLocation, 1 ) }}km away
            </div>

            <div class="pb-12 bg-gradient-to-b from-transparent via-[10rem] via-gray-850 to-gray-900">
            
                <!-- ACTION BUTTONS -->
                <div v-if="model.hasLink"
                     class="gap-x-4 3xl:gap-x-6 px-4 3xl:px-6">

                    <!-- THE OPEN BUTTON -->
                    <JupiterButton :color="offerTile.isGiftCard ? '#2260d4' : offerTile.isAutomotive ? 'secondary' : '#f16d15'"
                                   variant="flat"
                                   class="rounded-[5px] lg:!h-12 3xl:!h-14 min-w-[50%]"
                                   type="url"
                                   :to="model.linkUrl">
                        <span v-if="model.hasBookingLink" class="flex items-center gap-1.5">
                            <Icon name="calendar" />
                            <span class="text-lg 3xl:text-xl">Book Now</span>
                            <Icon name="arrow-right" />
                        </span>
                        <span v-else-if="model.isGiftCard" class="flex items-center gap-1.5">
                            <span class="text-lg 3xl:text-xl">Get This Gift Card</span>
                            <Icon name="arrow-right" />
                        </span>
                        <span v-else class="flex items-center gap-1.5">
                            <span class="text-lg 3xl:text-xl">Open</span>
                            <Icon name="arrow-right" />
                        </span>
                    </JupiterButton>

                </div>

                <!-- USAGE DETAILS -->
                <div class="col-span-2 opacity-60 
                            text-base 3xl:text-lg tracking-wider leading-tight 
                            px-4 3xl:px-6 pt-6 pb-8"
                     :class="{ '-mt-8': model.hasLink == false }">
                    <div v-if="model.isCardLinked && model.isOnlineOffer">
                        This is an online offer.  You can take up the offer from the app or online 
                        as long as you make the payment using the credit card linked to this app.
                    </div>
                    <div v-else-if="model.isCardLinked && model.isInstoreOffer">
                        This is an in-store offer.  You can take up the offer in store at one of the locations
                        below, when making payment using the credit card linked to this app.
                    </div>
                    <div v-else-if="model.isGiftCard">
                        This is an in-app offer.  You can purchase the Gift Card by pressing the button above.
                    </div>
                    <div v-else-if="model.isCardLinked == false && model.isOnlineOffer">
                        This is an in-app offer.  You can take up the offer by pressing the Open button above.
                    </div>
                </div>

                <div class="tracking-wider">  

                    <div class="px-4 3xl:px-6">

                        <!-- DESCRIPTION -->
                        <h1 class="text-lg 3xl:text-2xl font-bold pb-1 tracking-wider">
                            Description
                        </h1>

                        <p v-for="line in model.description"
                           class="opacity-60 leading-snug tracking-wider text-base 3xl:text-lg"> 
                            {{ line }}
                        </p>
                         
                    </div>

                    <!-- LOCATIONS -->
                    <div v-if="model.locations.length > 0"
                         class="mt-8 flex flex-col w-full gap-2">

                        <h1 class="px-4 3xl:px-6 text-lg 3xl:text-2xl font-bold pb-1 tracking-wider">
                            Locations
                        </h1>

                        <OfferLocation v-for="(location, index) in model.locations ?? []" 
                                       :view-model="viewModel"
                                       v-model="model.locations[ index ]" />
                    </div>

                    <!-- AVAILABILITY -->
                    <h1 class="px-4 3xl:px-6 mt-8 text-lg 3xl:text-2xl font-bold pb-1">
                        Availability<span class="opacity-60" v-if="model.isEndingSoon">*</span>
                    </h1>

                    <div class="px-4 3xl:px-6 text-base pb-1">
                        <div v-if="model.utcStartDate">
                            <span class="opacity-60">Available from </span> {{ formatDate( model.utcStartDate, 'Do MMMM YYYY' ) }}
                        </div>

                        <div v-else>
                            Available now
                        </div>

                        <div v-if="model.utcEndDate">
                            <span class="opacity-60">Offer ends </span>{{ formatDate( model.utcEndDate, 'Do MMMM YYYY' ) }}
                        </div>
                    </div>

                    <!-- MORE INFORMATION -->
                    <h1 class="px-4 3xl:px-6 mt-8 text-lg 3xl:text-2xl font-bold pb-1">
                        More Information
                    </h1>

                    <!-- HOW IT WORKS -->
                    <div v-if="model.howItWorks.length > 0">
                        <OfferExpandableSection>
                            <template #title>How it works</template>
                            <p v-for="line in model.howItWorks"
                               class="opacity-60 leading-snug text-md text-base 3xl:text-lg mb-0.5">
                                {{ line }}
                            </p>
                        </OfferExpandableSection>
                    </div>

                    <!-- ABOUT ADVERTISER -->
                    <div v-if="model.advertiserAboutUs.length > 0">
                        <OfferExpandableSection>
                            <template #title>About {{ model.advertiserName }}</template>
                            <p v-for="line in model.advertiserAboutUs"
                               class="opacity-60 leading-snug text-base 3xl:text-lg mb-0.5">
                                {{ line }}
                            </p>                
                        </OfferExpandableSection>
                    </div>

                    <!-- TERMS AND CONDITIONS -->
                    <div v-if="model.terms.length > 0">
                        <OfferExpandableSection>
                            <template #title>Terms and conditions</template>
                            <p v-for="line in model.terms"
                               class="opacity-60 leading-snug text-base 3xl:text-lg mb-0.5">
                                {{ line }}
                            </p>                
                        </OfferExpandableSection>
                    </div>

                </div>

            </div>

        </div>
        
    </div>
    
</template>